import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import {
  borderRadius,
  borderRadiusSizes,
  colorConstants,
  colors,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { isBrowser } from '@jarvis.oddle/core/env'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { pixelise } from '@jarvis.oddle/core/tools/style'
import Div from '@jarvis.oddle/core/components/Div'
import Carousel from '@jarvis.oddle/core/components/Carousel'
import Image from '@jarvis.oddle/core/components/Image'
import { useAppContext } from 'src/containers/AppContext'
import { isEmpty } from 'src/utils/string'
import {
  checkExistNutriGradeTag,
  NutriGradeIcon
} from 'src/components/icons/NutriGrade'

const thumbnailSize = 70
export const imageSize = 340

const Container = styled(Div)<{
  withThumbnails: boolean
}>`
  flex-shrink: 0;
  overflow: hidden;
  ${breakpoint('mobile')`
    width: 100vw;
    height: calc(100vw + ${(props) =>
      props.withThumbnails ? `${pixelise(thumbnailSize)} + ` : ''} ${
    margin[marginSizes.extraSmall]
  } + ${padding[paddingSizes.extraSmall]});
  `}
  ${breakpoint('desktop')`
    width: ${imageSize}px;
    height: calc(${pixelise(imageSize)} + ${(props) =>
    props.withThumbnails ? `${pixelise(thumbnailSize)} + ` : ''} ${
    margin[marginSizes.extraSmall]
  } + ${padding[paddingSizes.extraSmall]});
  `}
`

const ImageContainer = styled(Div)`
  background: ${colors.background};
  outline: none;
`

const ProductImageContainer = styled(ImageContainer)`
  position: relative;
  ${breakpoint('mobile')`
    width: 100vw !important; /* Hot fix variableWidth not working */
    height: 100vw;
  `}
  ${breakpoint('desktop')`
    width: ${imageSize}px !important; /* Hot fix variableWidth not working */
    height: ${imageSize}px;
    border-radius: ${borderRadius[borderRadiusSizes.medium]} ${
    borderRadius[borderRadiusSizes.medium]
  };
  `}
`
const CarouselContainer = styled(ProductImageContainer)`
  position: relative;
`

const OverlayContainer = styled(Div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const ThumbnailContainer = styled(ImageContainer)`
  width: ${thumbnailSize}px !important; /* Hot fix variableWidth not working */
  height: ${thumbnailSize}px;
  padding: ${padding[paddingSizes.extraSmall]};
  margin: ${margin[marginSizes.extraSmall]};

  .slick-current & {
    box-shadow: 0 0 0 2px ${colorConstants.black};
  }
`

const StyledProductImage = styled(Image).attrs(() => ({
  objectFit: 'contain'
}))<{
  hidden: boolean
  withCursor: boolean
}>`
  ${(props) => (props.hidden ? 'display: none;' : '')}
  ${(props) => (props.withCursor ? 'cursor: pointer;' : '')}
  width: 100%;
  height: 100%;
`

const loading = keyframes`
  100% {
    transform: translateX(100%);
  }
`

const ImageLoading = styled(Div)`
  height: 100%;
  width: 100%;
  position: relative;
  background-color: ${colorConstants.lightGray};

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );
    animation: ${loading} 0.5s infinite;
  }
`

const WrappedProductImage = styled.div`
  position: relative;
  display: inline;
`

const OverlayNutriGradeImage = styled.div<{
  hasCarousel?: boolean
  showImage?: boolean
}>`
  position: absolute;
  bottom: ${(props) => (props.hasCarousel ? '0' : '9px')};
  background: #f2f2f26b;
  width: 100%;
  transition: 0.5s ease;
  color: white;
  padding: 5px;
  padding-left: ${padding[paddingSizes.medium]};
  ${breakpoint('desktop')`
    ${(props) =>
      props.showImage
        ? `
      padding-left: ${padding[paddingSizes.extraLarge]};
      `
        : `
      padding-left: ${padding[paddingSizes.medium]};
      `}
  `}
  text-align: left;
  svg {
    position: relative;
    top: 3px;
  }
`

interface ProductImageProps {
  productState: Menu.MenuItem
  showDefaultImage: boolean
  showImage?: boolean
}

const ProductImage = ({
  productState,
  showDefaultImage,
  showImage
}: ProductImageProps): JSX.Element => {
  const [mainSlider, setMainSlider] = useState<null | Record<
    string,
    () => void
  >>(null)
  const [thumbnailsSlider, setThumbnailsSlider] = useState<null | Record<
    string,
    () => void
  >>(null)
  const mainSliderRef = useRef(null)
  const thumbnailsSliderRef = useRef(null)
  const { getProductImagePath } = useAppContext()
  const existNutriGradeTag = checkExistNutriGradeTag(productState.tags)

  useEffect(() => {
    setMainSlider(mainSliderRef.current)
    setThumbnailsSlider(thumbnailsSliderRef.current)
  }, [])

  const imageFullUrls =
    productState.imageFullUrls?.filter((image) => !isEmpty(image))?.length > 0
      ? productState.imageFullUrls
      : productState.imageUrls?.filter((image) => !isEmpty(image))?.length > 0
      ? productState.imageUrls
      : [null]
  const allImages = imageFullUrls.map((imageUrl) =>
    getProductImagePath(imageUrl, showDefaultImage, {
      shouldCache: true,
      containerHeight: 1000
    })
  )

  const images = isBrowser() ? allImages : [allImages[0]]

  if (!images.includes(null)) {
    return (
      <Container withThumbnails={allImages.length > 1}>
        <WrappedProductImage>
          <StyledProductImage
            hidden={mainSlider}
            src={images[0]}
            alt={productState.name}
          />
          {images[0] &&
            !images[0].includes('default-product.png') &&
            existNutriGradeTag &&
            showImage && (
              <OverlayNutriGradeImage showImage={showImage}>
                <NutriGradeIcon
                  tags={productState.tags}
                  height={60}
                  width={119}
                />
              </OverlayNutriGradeImage>
            )}
        </WrappedProductImage>
        <CarouselContainer>
          {mainSlider && (
            <OverlayContainer>
              <ImageLoading />
            </OverlayContainer>
          )}
          <Carousel
            innerRef={(slider) => {
              mainSliderRef.current = slider
            }}
            slidesToShow={1}
            slidesToScroll={1}
            dots={false}
            arrows={false}
            asNavFor={thumbnailsSlider}
          >
            {images.map((image) => (
              <ProductImageContainer key={image}>
                <StyledProductImage src={image} alt={productState.name} />
                {((images[0] && !images[0].includes('default-product.png')) ||
                  !showImage) &&
                  existNutriGradeTag && (
                    <OverlayNutriGradeImage hasCarousel={images.length > 1}>
                      <NutriGradeIcon
                        tags={productState.tags}
                        height={60}
                        width={119}
                      />
                    </OverlayNutriGradeImage>
                  )}
              </ProductImageContainer>
            ))}
          </Carousel>
        </CarouselContainer>
        {/* NOTE: Need to manually check https://oddle-pm.atlassian.net/browse/FESC-1257 for any regression when thumbnail carousel is changed.  */}
        {images.length > 1 && (
          <Carousel
            innerRef={(slider) => {
              thumbnailsSliderRef.current = slider
            }}
            slidesToShow={4}
            slidesToScroll={1}
            dots={false}
            arrows={false}
            focusOnSelect
            variableWidth
            swipeToSlide
            asNavFor={mainSlider}
          >
            {images.map((image) => (
              <ThumbnailContainer key={image}>
                <StyledProductImage
                  withCursor
                  src={image}
                  alt={productState.name}
                />
              </ThumbnailContainer>
            ))}
          </Carousel>
        )}
      </Container>
    )
  }

  return null
}

export default ProductImage
